<template>
  <section class="intro">
    <div class="intro__inner">
      <div class="bento">
        <!-- Hook -->
        <article class="cell cell--hook">
          <span class="eyebrow eyebrow--accent">Post-workout coaching</span>
          <div class="cell--hook__body">
            <h2 class="hook__title">
              You finished the ride.
              <br class="lg:block hidden" />
              But did it
              <span style="color: #eb695c">make you faster?</span>
            </h2>
            <p class="hook__sub">
              Your head unit shows you numbers. FormBeat tells you what they mean - clear, actionable feedback on every ride, and what to sharpen next.
            </p>
          </div>
          <div class="debrief">
            <span class="debrief__mark"></span>
            <span>Strong aerobic control: decoupling held under 4% to the finish.</span>
          </div>
        </article>

        <!-- Live insight (mobile only — desktop shows the floating hero card) -->
        <article class="cell cell--insight">
          <HomepageHeroInsight variant="static" />
        </article>

        <!-- Coaching -->
        <article class="cell cell--coach">
          <span class="eyebrow">After every ride</span>
          <h3 class="cell__title">Coaching, not congratulations.</h3>
          <p class="cell__body">Every ride comes back as a plain-English read of what the session did for your fitness, and what to do next.</p>
          <div class="metrics">
            <div class="metric">
              <span class="metric__v">0.88</span>
              <span class="metric__k">Intensity</span>
            </div>
            <div class="metric">
              <span class="metric__v">1.05</span>
              <span class="metric__k">Variability</span>
            </div>
            <div class="metric">
              <span class="metric__v">
                3.2
                <small>%</small>
              </span>
              <span class="metric__k">Decoupling</span>
            </div>
          </div>
        </article>

        <!-- Today -->
        <article class="cell cell--today">
          <span class="eyebrow">Today</span>
          <h3 class="cell__title">Know what to ride today</h3>
          <div class="ready">
            <span class="ready__dot"></span>
            <span class="ready__label">Ready to push</span>
          </div>
          <p class="ready__suggestion">Tempo intervals · ~75 TSS</p>
        </article>

        <!-- Progress -->
        <article class="cell cell--progress">
          <span class="eyebrow">Over time</span>
          <h3 class="cell__title">Progress you can see.</h3>
          <svg class="spark" viewBox="0 0 120 44" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="fbSparkFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#b6ffc9" stop-opacity="0.3" />
                <stop offset="100%" stop-color="#b6ffc9" stop-opacity="0" />
              </linearGradient>
            </defs>
            <path d="M0,38 L15,35 L30,37 L45,28 L60,30 L75,20 L90,22 L105,12 L120,6 L120,44 L0,44 Z" fill="url(#fbSparkFill)" />
            <path
              d="M0,38 L15,35 L30,37 L45,28 L60,30 L75,20 L90,22 L105,12 L120,6"
              fill="none"
              stroke="#b6ffc9"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <p class="progress__caption">
            FTP
            <span class="delta">+17W</span>
            · 90 days
          </p>
        </article>

        <!-- Multisport -->
        <article class="cell cell--multi">
          <div class="multi__lead">
            <span class="eyebrow">Multisport</span>
            <h3 class="cell__title">Every sport, one picture.</h3>
            <p class="cell__body">
              Track your training load and fitness across multiple sport types - each session analyzed by type and pulled into a single, complete view.
            </p>
          </div>
          <div class="multi__sports">
            <div class="sport sport--bike">
              <span class="sport__icon">
                <svg class="svg-icon"><use xlink:href="#icon-cycling"></use></svg>
              </span>
              <span class="sport__name">Bike</span>
            </div>
            <div class="sport sport--run">
              <span class="sport__icon">
                <svg class="svg-icon"><use xlink:href="#icon-running"></use></svg>
              </span>
              <span class="sport__name">Run</span>
            </div>
            <div class="sport sport--swim">
              <span class="sport__icon">
                <svg class="svg-icon"><use xlink:href="#icon-swimming"></use></svg>
              </span>
              <span class="sport__name">Swim</span>
            </div>
            <div class="sport sport--row">
              <span class="sport__icon">
                <svg class="svg-icon"><use xlink:href="#icon-sport-kayak"></use></svg>
              </span>
              <span class="sport__name">Rowing</span>
            </div>
            <div class="sport sport--strength">
              <span class="sport__icon">
                <svg class="svg-icon"><use xlink:href="#icon-sport-weight"></use></svg>
              </span>
              <span class="sport__name">Strength</span>
            </div>
            <div class="sport sport--more">
              <span class="sport__icon sport__icon--ghost">+</span>
              <span class="sport__name">More</span>
            </div>
          </div>
        </article>
      </div>

      <figure class="press">
        <img class="press__logo" src="~images/logos/roadcc.svg" alt="Road.cc" />
        <blockquote class="press__quote">
          “If you find yourself wishing there was just one simple resource you could rely on to fully explain, examine and guide your training, FormBeat could
          be your answer.”
        </blockquote>
      </figure>
    </div>
  </section>
</template>

<script>
import HomepageHeroInsight from 'components/HomepageHeroInsight'

export default {
  name: 'HomepageIntro',
  components: { HomepageHeroInsight }
}
</script>

<style scoped>
.intro {
  position: relative;
  overflow: hidden;
  padding: 6rem 1.5rem;
  color: #e8eef2;
  /* background: radial-gradient(125% 120% at 12% -10%, #0d0d2c 0%, #1e1e54 50%, #0c0c2a 100%); */
  background: #001116;
}

/* fine grain overlay */
.intro::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.05;
  /* background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); */
}

.intro__inner {
  position: relative;
  z-index: 1;
  max-width: 90rem;
  margin: 0 auto;
}

.bento {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

@media (min-width: 768px) {
  .intro {
    padding: 7.5rem 2rem;
  }
  .bento {
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: minmax(200px, 1fr);
  }
  .cell--hook {
    grid-column: span 2;
    grid-row: span 2;
  }
  .cell--coach {
    grid-column: span 2;
  }
  .cell--multi {
    grid-column: span 4;
  }
}

/* --- cells --- */
.cell {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1.75rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.012));
  transition: border-color 0.35s ease, background 0.35s ease, box-shadow 0.35s ease;
}
.cell:hover {
  border-color: rgba(255, 255, 255, 0.16);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.02));
  box-shadow: 0 24px 60px -32px rgba(0, 0, 0, 0.8);
}
/* Live insight card — mobile only; desktop shows the floating hero insight instead.
   Hidden here (scoped) rather than via a utility, which .cell would out-specify. */
.cell--insight {
  justify-content: center;
}
@media (min-width: 768px) {
  .cell--insight {
    display: none;
  }
}

.cell--hook {
  padding: 2.25rem;
  justify-content: flex-start;
  background: radial-gradient(95% 85% at 85% 105%, rgba(252, 76, 1, 0.18), transparent 58%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.015));
}

/* --- type --- */
.eyebrow {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.8rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(232, 238, 242, 0.62);
}

.hook__title {
  margin: 1.4rem 0 0;
  font-size: clamp(1.85rem, 3.6vw, 2.85rem);
  font-weight: 600;
  line-height: 1.06;
  letter-spacing: -0.025em;
}
.hook__sub {
  margin-top: 1rem;
  max-width: 38ch;
  font-size: 1.15rem;
  line-height: 1.65;
  color: rgba(232, 238, 242, 0.8);
}

.debrief {
  display: flex;
  align-items: center;
  margin-top: auto;
  gap: 0.65rem;
  /* margin-top: 1.75rem; */
  padding: 0.7rem 0.9rem;
  font-size: 0.88rem;
  line-height: 1.4;
  color: rgba(232, 238, 242, 0.82);
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px;
}
.debrief__mark {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #b6ffc9;
  box-shadow: 0 0 10px rgba(182, 255, 201, 0.7);
}

.cell__title {
  margin-top: 0.55rem;
  font-size: 1.66rem;
  font-weight: 600;
  letter-spacing: -0.015em;
}
.cell__body {
  margin-top: 0.6rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(232, 238, 242, 0.58);
}

/* --- coaching metrics --- */
.metrics {
  display: flex;
  gap: 2rem;
  margin-top: auto;
  padding-top: 1.5rem;
}
.metric {
  display: flex;
  flex-direction: column;
}
.metric__v {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}
.metric__v small {
  font-size: 0.85rem;
  color: rgba(232, 238, 242, 0.5);
}
.metric__k {
  margin-top: 0.25rem;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(232, 238, 242, 0.42);
}

/* --- today readiness --- */
.ready {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: auto;
}
.ready__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #b6ffc9;
  box-shadow: 0 0 14px rgba(182, 255, 201, 0.75);
}
.ready__label {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #b6ffc9;
}
.ready__suggestion {
  margin-top: 0.55rem;
  font-size: 0.95rem;
  color: rgba(232, 238, 242, 0.8);
}

/* --- progress sparkline --- */
.spark {
  width: 100%;
  height: 46px;
  margin-top: auto;
}
.progress__caption {
  margin-top: 0.75rem;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  color: rgba(232, 238, 242, 0.55);
}
.delta {
  color: #ff5933;
  font-weight: 600;
}

/* --- multisport --- */
.cell--multi {
  flex-direction: column;
  gap: 2rem;
}
@media (min-width: 768px) {
  .cell--multi {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 3rem;
    padding: 2.25rem;
  }
  .multi__lead {
    max-width: 38%;
  }
}
.multi__sports {
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  justify-content: center;
  gap: 1.1rem;
}
.sport {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
}
.sport--bike {
  --sport: #fc4c01;
}
.sport--run {
  --sport: #ff6b6b;
}
.sport--swim {
  --sport: #38bdf8;
}
.sport--row {
  --sport: #2dd4bf;
}
.sport--strength {
  --sport: #a78bfa;
}
.sport__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  color: var(--sport, rgba(232, 238, 242, 0.82));
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid var(--sport, rgba(255, 255, 255, 0.12));
  border-radius: 15px;
  transition: box-shadow 0.3s ease, background 0.3s ease, transform 0.3s ease;
}
.sport:hover .sport__icon {
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 30px -12px var(--sport, rgba(0, 0, 0, 0.6));
  transform: translateY(-2px);
}
.sport__icon--ghost {
  font-size: 1.4rem;
  font-weight: 300;
  color: rgba(232, 238, 242, 0.5);
  border-color: rgba(255, 255, 255, 0.18);
  border-style: dashed;
}
.sport__name {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(232, 238, 242, 0.5);
}
.sport--lead .sport__name {
  color: #fc4c01;
}

/* --- press quote --- */
.press {
  max-width: 760px;
  margin: 4.5rem auto 0;
  text-align: center;
}
.press__logo {
  display: block;
  width: 112px;
  height: auto;
  margin: 0 auto 1.75rem;
}
.press__quote {
  margin: 0;
  font-size: clamp(1.2rem, 2.1vw, 1.65rem);
  font-weight: 300;
  font-style: italic;
  line-height: 1.5;
  letter-spacing: -0.01em;
  color: rgba(255, 255, 255, 0.9);
}

@media (prefers-reduced-motion: reduce) {
  .cell {
    transition: none;
  }
}
</style>
